export type OperationIcon =
  | 'dashboard'
  | 'store'
  | 'coffee'
  | 'truck'
  | 'users'
  | 'award'
  | 'gift'
  | 'chart'
  | 'wallet'
  | 'bell'
  | 'settings'
  | 'bag'
  | 'drone'

export interface OperationMenuItem {
  name: string
  path: string
  badge?: number
}

export interface OperationMenuGroup {
  title: string
  icon: OperationIcon
  items: OperationMenuItem[]
}

export interface OperationStat {
  label: string
  value: string
  sub: string
  tone?: 'brand' | 'success' | 'warning' | 'error'
}

export interface OperationFilter {
  type: 'input' | 'select'
  placeholder: string
  options?: string[]
}

export interface OperationColumn {
  key: string
  label: string
  align?: 'left' | 'right'
  width?: string
  toneMap?: Record<string, 'brand' | 'success' | 'warning' | 'error' | 'muted'>
}

export interface OperationCard {
  title: string
  status?: string
  meta: string[]
  footer?: string
}

export interface OperationPageConfig {
  title: string
  action?: string
  stats?: OperationStat[]
  filters?: OperationFilter[]
  columns?: OperationColumn[]
  rows?: Record<string, string>[]
  cards?: OperationCard[]
}

export const operationMenus: OperationMenuGroup[] = [
  {
    title: '运营总览',
    icon: 'dashboard',
    items: [
      { name: '工作台', path: '/dashboard' },
    ],
  },
  {
    title: '交易履约',
    icon: 'bag',
    items: [
      { name: '订单中心', path: '/orders' },
      { name: '退款处理', path: '/refunds' },
      { name: '投诉建议', path: '/complaints' },
    ],
  },
  {
    title: '门店管理',
    icon: 'store',
    items: [
      { name: '门店列表', path: '/stores' },
      { name: '打印设备', path: '/stores/printers' },
    ],
  },
  {
    title: '商品中心',
    icon: 'coffee',
    items: [
      { name: '商品分类', path: '/products/categories' },
      { name: '商品列表', path: '/products' },
      { name: '商品套餐', path: '/products/combos' },
      { name: '加料管理', path: '/products/extras' },
    ],
  },
  {
    title: '配送与计价',
    icon: 'truck',
    items: [
      { name: '起送金额', path: '/delivery/min-amount' },
      { name: '门店配送范围', path: '/delivery/areas' },
      { name: '配送点管理', path: '/delivery/points' },
    ],
  },
  {
    title: '无人机管理',
    icon: 'drone',
    items: [
      { name: '无人机设备', path: '/drones/drones' },
      { name: '任务调度', path: '/drones/tasks' },
      { name: '飞行记录', path: '/drones/flights' },
      { name: '故障管理', path: '/drones/faults' },
      { name: '维护管理', path: '/drones/maintenances' },
    ],
  },
  {
    title: '用户管理',
    icon: 'users',
    items: [
      { name: '用户管理', path: '/users' },
    ],
  },
  {
    title: '会员中心',
    icon: 'award',
    items: [
      { name: '会员等级', path: '/members/levels' },
      { name: '规则配置', path: '/members/rules' },
      { name: '会员权益', path: '/members/benefits' },
    ],
  },
  {
    title: '营销中心',
    icon: 'gift',
    items: [
      { name: '优惠券', path: '/marketing/coupons' },
      { name: '积分商城', path: '/marketing/points-mall' },
      { name: '充值套餐', path: '/marketing/recharge' },
      { name: '首页 Banner', path: '/marketing/banners' },
      { name: '拼团活动', path: '/marketing/group-buy' },
      { name: '砍价活动', path: '/marketing/bargain' },
      { name: '限时秒杀', path: '/marketing/flash-sale' },
    ],
  },
  {
    title: '数据中心',
    icon: 'chart',
    items: [
      { name: '经营概览', path: '/data/overview' },
      { name: '订单分析', path: '/data/orders' },
      { name: '商品分析', path: '/data/products' },
      { name: '门店排行', path: '/data/store-rank' },
    ],
  },
  {
    title: '财务与支付',
    icon: 'wallet',
    // 支付配置（/finance/payment-config）路由与页面都还在（见 router/routes.ts），
    // 但**刻意不进菜单**：该页维护的是微信支付商户号/API 密钥，属于一次性开通配置，
    // 不是日常运营动作。routes.test.ts 里有断言钉住这个隐藏行为，改这里必须同步改测试。
    items: [
      { name: '交易流水', path: '/finance/transactions' },
      { name: '门店对账', path: '/finance/reconciliation' },
      { name: '退款流水', path: '/finance/refunds' },
      { name: '微信分账', path: '/finance/profit-sharing' },
      { name: '分账规则', path: '/finance/profit-sharing/rules' },
      { name: '分账接收方', path: '/finance/profit-sharing/receivers' },
    ],
  },
  {
    title: '系统管理',
    icon: 'settings',
    items: [
      { name: '管理员', path: '/system/admins' },
      { name: '角色权限', path: '/system/roles' },
      { name: '操作日志', path: '/system/logs' },
      { name: '系统参数', path: '/system/params' },
    ],
  },
]

export const dashboardStats: OperationStat[] = [
  { label: '今日营业额', value: '¥12,450.00', sub: '+12.5%', tone: 'success' },
  { label: '今日订单数', value: '456', sub: '+8.2%', tone: 'success' },
  { label: '实付用户数', value: '389', sub: '+5.4%', tone: 'success' },
  { label: '客单价', value: '¥27.30', sub: '-1.2%', tone: 'brand' },
  { label: '退款金额', value: '¥345.00', sub: '+2.1%', tone: 'error' },
  { label: '待处理订单', value: '12', sub: '需要关注', tone: 'warning' },
]

export const trendData = [
  { name: '10-01', value: 8400 },
  { name: '10-02', value: 9200 },
  { name: '10-03', value: 10500 },
  { name: '10-04', value: 9800 },
  { name: '10-05', value: 11200 },
  { name: '10-06', value: 12450 },
  { name: '10-07', value: 13000 },
]

export const pieData = [
  { name: '待支付', value: 400, color: '#c8cdd2' },
  { name: '制作中', value: 300, color: '#f2a950' },
  { name: '配送中', value: 300, color: '#76b7e8' },
  { name: '已完成', value: 2000, color: '#45b97c' },
  { name: '已退款', value: 100, color: '#e76565' },
]

export const storeRank = [
  { name: '交发云咖（高新科技园店）', amount: '¥3,450.00', orders: '120', price: '¥28.75', trend: '+15%' },
  { name: '交发云咖（万象城店）', amount: '¥2,890.00', orders: '98', price: '¥29.48', trend: '+8%' },
  { name: '交发云咖（大学城店）', amount: '¥2,100.00', orders: '85', price: '¥24.70', trend: '-2%' },
  { name: '交发云咖（软件园店）', amount: '¥1,950.00', orders: '72', price: '¥27.08', trend: '+5%' },
]

export const productRank = [
  {
    name: '招牌生椰拿铁',
    sales: '156',
    amount: '¥2,964.00',
    img: 'https://images.unsplash.com/photo-1572442388796-11668a67e53d?w=100&h=100&fit=crop',
  },
  {
    name: '多肉葡萄冰沙',
    sales: '124',
    amount: '¥3,100.00',
    img: 'https://images.unsplash.com/photo-1556881286-fc6915169721?w=100&h=100&fit=crop',
  },
  {
    name: '经典珍珠奶茶',
    sales: '98',
    amount: '¥1,470.00',
    img: 'https://images.unsplash.com/photo-1558857563-b37102e99e2a?w=100&h=100&fit=crop',
  },
]

const commonOrderFilters: OperationFilter[] = [
  { type: 'input', placeholder: '输入订单号/手机号' },
  { type: 'select', placeholder: '全部分店', options: ['全部分店', '高新科技园店', '万象城店'] },
  { type: 'select', placeholder: '全部状态', options: ['全部状态', '已完成', '待取餐', '制作中'] },
  { type: 'select', placeholder: '全部类型', options: ['全部类型', '自提', '外卖'] },
]

const orderColumns: OperationColumn[] = [
  { key: 'id', label: '订单号' },
  { key: 'store', label: '门店' },
  { key: 'user', label: '用户' },
  { key: 'items', label: '商品摘要', width: '160px' },
  { key: 'type', label: '类型', toneMap: { 自提: 'brand', 外卖: 'warning' } },
  { key: 'amount', label: '实付 / 优惠' },
  { key: 'payType', label: '支付方式' },
  {
    key: 'status',
    label: '状态',
    toneMap: { 已完成: 'success', 配送中: 'brand', 待取餐: 'warning', 制作中: 'brand', 已退款: 'error' },
  },
  { key: 'time', label: '下单时间' },
]

const orderRows = [
  {
    id: 'DD202310240001',
    store: '高新科技园店',
    user: '王先生 (8902)',
    items: '招牌生椰拿铁 等2件',
    type: '自提',
    amount: '¥36.00 / ¥4.00',
    payType: '微信支付',
    status: '已完成',
    time: '2023-10-24 10:45:22',
  },
  {
    id: 'DD202310240002',
    store: '万象城店',
    user: '李女士 (1102)',
    items: '多肉葡萄冰沙',
    type: '外卖',
    amount: '¥22.00 / ¥0.00',
    payType: '支付宝',
    status: '配送中',
    time: '2023-10-24 10:42:10',
  },
  {
    id: 'DD202310240003',
    store: '高新科技园店',
    user: '张同学 (4451)',
    items: '鸭屎香柠檬茶',
    type: '自提',
    amount: '¥16.00 / ¥2.00',
    payType: '余额支付',
    status: '待取餐',
    time: '2023-10-24 10:35:05',
  },
  {
    id: 'DD202310240004',
    store: '大学城店',
    user: '赵先生 (9928)',
    items: '厚乳拿铁 等3件',
    type: '外卖',
    amount: '¥58.00 / ¥10.00',
    payType: '微信支付',
    status: '已退款',
    time: '2023-10-24 10:20:11',
  },
  {
    id: 'DD202310240005',
    store: '软件园店',
    user: '陈女士 (3321)',
    items: '经典珍珠奶茶',
    type: '自提',
    amount: '¥15.00 / ¥0.00',
    payType: '微信支付',
    status: '制作中',
    time: '2023-10-24 10:15:33',
  },
]

export const operationPageConfigs: Record<string, OperationPageConfig> = {
  orderCenter: {
    title: '订单中心',
    action: '导出报表',
    stats: [
      { label: '今日支付订单', value: '1,245', sub: '较昨日 +12%', tone: 'success' },
      { label: '今日退款订单', value: '12', sub: '较昨日 -2%', tone: 'error' },
      { label: '客单价', value: '¥28.5', sub: '较昨日 +¥1.2', tone: 'brand' },
    ],
    filters: commonOrderFilters,
    columns: orderColumns,
    rows: orderRows,
  },
  verification: {
    title: '核销管理',
    action: '批量核销',
    stats: [
      { label: '待核销', value: '48', sub: '今日新增 22', tone: 'warning' },
      { label: '已核销', value: '326', sub: '核销率 87.2%', tone: 'success' },
      { label: '异常核销', value: '3', sub: '需要复核', tone: 'error' },
    ],
    filters: [
      { type: 'input', placeholder: '输入核销码/订单号' },
      { type: 'select', placeholder: '核销状态', options: ['核销状态', '待核销', '已核销', '异常'] },
    ],
    columns: [
      { key: 'code', label: '核销码' },
      { key: 'order', label: '关联订单' },
      { key: 'store', label: '门店' },
      { key: 'user', label: '用户' },
      { key: 'status', label: '状态', toneMap: { 待核销: 'warning', 已核销: 'success', 异常: 'error' } },
      { key: 'time', label: '核销时间' },
    ],
    rows: [
      { code: 'HX-901248', order: 'DD202310240001', store: '高新科技园店', user: '王先生', status: '已核销', time: '10:46:12' },
      { code: 'HX-901249', order: 'DD202310240006', store: '万象城店', user: '刘女士', status: '待核销', time: '-' },
      { code: 'HX-901250', order: 'DD202310240008', store: '大学城店', user: '周同学', status: '异常', time: '10:30:18' },
    ],
  },
  refunds: {
    title: '退款处理',
    action: '导出退款单',
    stats: [
      { label: '待处理退款', value: '8', sub: '超时 1 单', tone: 'warning' },
      { label: '今日退款金额', value: '¥345.00', sub: '较昨日 -2%', tone: 'error' },
      { label: '平均处理时长', value: '3m 28s', sub: '效率稳定', tone: 'success' },
    ],
    filters: [
      { type: 'input', placeholder: '输入退款单/订单号' },
      { type: 'select', placeholder: '退款状态', options: ['退款状态', '待审核', '已退款', '已拒绝'] },
    ],
    columns: [
      { key: 'id', label: '退款单号' },
      { key: 'order', label: '订单号' },
      { key: 'reason', label: '退款原因' },
      { key: 'amount', label: '退款金额' },
      { key: 'status', label: '状态', toneMap: { 待审核: 'warning', 已退款: 'success', 已拒绝: 'error' } },
      { key: 'time', label: '申请时间' },
    ],
    rows: [
      { id: 'TK20231024001', order: 'DD202310240004', reason: '配送超时', amount: '¥58.00', status: '已退款', time: '10:20:11' },
      { id: 'TK20231024002', order: 'DD202310240015', reason: '商品售罄', amount: '¥24.00', status: '待审核', time: '10:12:09' },
      { id: 'TK20231024003', order: 'DD202310240021', reason: '用户误拍', amount: '¥16.00', status: '已拒绝', time: '09:52:43' },
    ],
  },
  stores: {
    title: '门店列表',
    action: '新增门店',
    filters: [
      { type: 'input', placeholder: '搜索门店名称/编码' },
      { type: 'select', placeholder: '营业状态', options: ['营业状态', '营业中', '休息中', '已闭店'] },
    ],
    cards: [
      {
        title: '高新科技园店',
        status: '营业中',
        meta: ['门店编码: GX-001', '高新区科技园A区1栋1楼', '张店长 13800138001'],
        footer: '营业时间: 08:00 - 22:00',
      },
      {
        title: '万象城店',
        status: '营业中',
        meta: ['门店编码: WX-002', '万象城购物中心B1层', '李店长 13900139002'],
        footer: '营业时间: 10:00 - 22:00',
      },
      {
        title: '大学城店',
        status: '休息中',
        meta: ['门店编码: DX-003', '大学城北区学生街3号', '王店长 13700137003'],
        footer: '营业时间: 09:00 - 23:00',
      },
      {
        title: '软件园店',
        status: '营业中',
        meta: ['门店编码: RJ-004', '软件园二期C区食堂旁', '赵店长 13600136004'],
        footer: '营业时间: 08:00 - 20:00',
      },
    ],
  },
  printers: {
    title: '打印设备',
    action: '绑定设备',
    columns: [
      { key: 'name', label: '设备名称' },
      { key: 'store', label: '所属门店' },
      { key: 'sn', label: '设备编号' },
      { key: 'status', label: '状态', toneMap: { 在线: 'success', 离线: 'error', 缺纸: 'warning' } },
      { key: 'last', label: '最后心跳' },
    ],
    rows: [
      { name: '前台小票机-01', store: '高新科技园店', sn: 'PRINT-GX-001', status: '在线', last: '10:45:01' },
      { name: '后厨标签机-01', store: '万象城店', sn: 'PRINT-WX-002', status: '缺纸', last: '10:41:28' },
      { name: '前台小票机-02', store: '大学城店', sn: 'PRINT-DX-003', status: '离线', last: '09:58:12' },
    ],
  },
  productCategories: {
    title: '商品分类',
    action: '新增分类',
    columns: [
      { key: 'name', label: '分类名称' },
      { key: 'count', label: '商品数' },
      { key: 'sort', label: '排序' },
      { key: 'status', label: '状态', toneMap: { 启用: 'success', 停用: 'muted' } },
      { key: 'time', label: '更新时间' },
    ],
    rows: [
      { name: '咖啡系列', count: '24', sort: '1', status: '启用', time: '10-24 10:12' },
      { name: '果茶冰沙', count: '18', sort: '2', status: '启用', time: '10-23 18:32' },
      { name: '轻食甜点', count: '12', sort: '3', status: '停用', time: '10-21 11:08' },
    ],
  },
  products: {
    title: '商品列表',
    action: '新增商品',
    stats: [
      { label: '在售商品', value: '126', sub: '较上周 +8', tone: 'success' },
      { label: '今日售罄', value: '6', sub: '需补货', tone: 'warning' },
      { label: '热销商品', value: '招牌生椰拿铁', sub: '156 杯', tone: 'brand' },
    ],
    filters: [
      { type: 'input', placeholder: '搜索商品名称/编码' },
      { type: 'select', placeholder: '商品分类', options: ['商品分类', '咖啡系列', '果茶冰沙', '轻食甜点'] },
      { type: 'select', placeholder: '销售状态', options: ['销售状态', '上架', '下架', '售罄'] },
    ],
    columns: [
      { key: 'name', label: '商品名称' },
      { key: 'category', label: '分类' },
      { key: 'price', label: '售价' },
      { key: 'stock', label: '库存' },
      { key: 'sales', label: '今日销量' },
      { key: 'status', label: '状态', toneMap: { 上架: 'success', 售罄: 'warning', 下架: 'muted' } },
    ],
    rows: [
      { name: '招牌生椰拿铁', category: '咖啡系列', price: '¥19.00', stock: '326', sales: '156', status: '上架' },
      { name: '多肉葡萄冰沙', category: '果茶冰沙', price: '¥25.00', stock: '118', sales: '124', status: '上架' },
      { name: '鸭屎香柠檬茶', category: '果茶冰沙', price: '¥16.00', stock: '0', sales: '82', status: '售罄' },
      { name: '厚乳拿铁', category: '咖啡系列', price: '¥21.00', stock: '86', sales: '76', status: '下架' },
    ],
  },
  productExtras: {
    title: '加料管理',
    action: '新增加料',
    columns: [
      { key: 'name', label: '加料名称' },
      { key: 'price', label: '加价' },
      { key: 'stock', label: '库存' },
      { key: 'products', label: '可用商品' },
      { key: 'status', label: '状态', toneMap: { 启用: 'success', 停用: 'muted' } },
    ],
    rows: [
      { name: '珍珠', price: '¥2.00', stock: '420', products: '36', status: '启用' },
      { name: '椰果', price: '¥2.00', stock: '350', products: '28', status: '启用' },
      { name: '燕麦奶', price: '¥3.00', stock: '96', products: '18', status: '停用' },
    ],
  },
  inventory: {
    title: '库存管理',
    action: '库存调整',
    stats: [
      { label: '库存预警', value: '12', sub: '低于安全库存', tone: 'warning' },
      { label: '售罄商品', value: '6', sub: '影响 4 家门店', tone: 'error' },
      { label: '今日入库', value: '386', sub: '杯/份', tone: 'success' },
    ],
    columns: [
      { key: 'product', label: '商品' },
      { key: 'store', label: '门店' },
      { key: 'stock', label: '当前库存' },
      { key: 'safe', label: '安全库存' },
      { key: 'status', label: '状态', toneMap: { 正常: 'success', 预警: 'warning', 售罄: 'error' } },
    ],
    rows: [
      { product: '招牌生椰拿铁', store: '高新科技园店', stock: '326', safe: '80', status: '正常' },
      { product: '鸭屎香柠檬茶', store: '万象城店', stock: '0', safe: '60', status: '售罄' },
      { product: '经典珍珠奶茶', store: '大学城店', stock: '42', safe: '60', status: '预警' },
    ],
  },
  productPricing: {
    title: '商品价格',
    action: '批量改价',
    columns: [
      { key: 'product', label: '商品' },
      { key: 'base', label: '总部价' },
      { key: 'store', label: '门店价' },
      { key: 'scope', label: '生效范围' },
      { key: 'status', label: '状态', toneMap: { 生效中: 'success', 待生效: 'warning' } },
    ],
    rows: [
      { product: '招牌生椰拿铁', base: '¥19.00', store: '¥19.00', scope: '全部门店', status: '生效中' },
      { product: '多肉葡萄冰沙', base: '¥25.00', store: '¥23.00', scope: '大学城店', status: '待生效' },
      { product: '厚乳拿铁', base: '¥21.00', store: '¥22.00', scope: '万象城店', status: '生效中' },
    ],
  },
  deliveryRules: {
    title: '配送规则',
    action: '新增规则',
    columns: [
      { key: 'name', label: '规则名称' },
      { key: 'store', label: '适用门店' },
      { key: 'fee', label: '配送费' },
      { key: 'time', label: '配送时段' },
      { key: 'status', label: '状态', toneMap: { 启用: 'success', 停用: 'muted' } },
    ],
    rows: [
      { name: '核心商圈 3km', store: '高新科技园店', fee: '¥3.00', time: '08:00 - 20:00', status: '启用' },
      { name: '购物中心专送', store: '万象城店', fee: '¥0.00', time: '10:00 - 22:00', status: '启用' },
      { name: '校园夜间配送', store: '大学城店', fee: '¥2.00', time: '18:00 - 23:00', status: '停用' },
    ],
  },
  deliveryMinAmount: {
    title: '起送金额',
    action: '保存规则',
    cards: [
      { title: '高新科技园店', status: '¥20 起送', meta: ['3km 内 ¥20', '3-5km ¥35', '满 ¥60 免配送费'], footer: '最近调整: 10-24 09:30' },
      { title: '万象城店', status: '¥0 起送', meta: ['商场内免起送', '商圈外 ¥25 起送', '满 ¥50 免配送费'], footer: '最近调整: 10-23 16:18' },
      { title: '大学城店', status: '¥15 起送', meta: ['校区内 ¥15', '夜间 ¥25', '满 ¥45 免配送费'], footer: '最近调整: 10-21 11:06' },
    ],
  },
  deliveryAreas: {
    title: '门店配送范围',
    action: '绘制范围',
    cards: [
      { title: '高新科技园店', status: '已配置', meta: ['覆盖半径 5km', '可配送楼宇 126 个', '预计送达 28 分钟'], footer: '高峰期自动缩圈已开启' },
      { title: '万象城店', status: '已配置', meta: ['覆盖半径 3km', '可配送楼宇 84 个', '预计送达 22 分钟'], footer: '商圈内优先派单' },
      { title: '大学城店', status: '待完善', meta: ['覆盖半径 4km', '可配送宿舍 32 栋', '预计送达 30 分钟'], footer: '部分区域需复核' },
    ],
  },
  deliveryPoints: {
    title: '配送点管理',
    action: '新增配送点',
    cards: [
      { title: '无人机固定送达点', status: '待接入', meta: ['用户地址自动匹配最近点位', '小程序展示全部可用配送点', '支持查看点位具体位置'], footer: '由后端配送点接口提供数据' },
    ],
  },
  droneDevices: {
    title: '无人机设备',
    action: '新增设备',
    stats: [
      { label: '设备总数', value: '0', sub: '等待台账同步', tone: 'brand' },
      { label: '在线设备', value: '0', sub: 'IDLE/TASK 视为在线', tone: 'success' },
      { label: '故障设备', value: '0', sub: '需运维处理', tone: 'error' },
    ],
    filters: [
      { type: 'input', placeholder: '搜索编号/名称' },
      { type: 'select', placeholder: '设备状态', options: ['设备状态', '空闲', '任务中', '充电中', '维护中', '故障', '离线'] },
      { type: 'select', placeholder: '在线状态', options: ['在线状态', '在线', '离线'] },
    ],
    columns: [
      { key: 'droneNo', label: '设备编号' },
      { key: 'name', label: '设备名称' },
      { key: 'model', label: '型号' },
      { key: 'battery', label: '电量' },
      { key: 'status', label: '状态', toneMap: { 空闲: 'success', 任务中: 'brand', 充电中: 'warning', 维护中: 'warning', 故障: 'error', 离线: 'muted' } },
      { key: 'lastCommAt', label: '最后通信' },
    ],
    rows: [],
  },
  droneTasks: {
    title: '任务调度',
    action: '刷新任务',
    filters: [
      { type: 'input', placeholder: '搜索配送单/订单号' },
      { type: 'select', placeholder: '任务状态', options: ['任务状态', '待调度', '装载中', '已起飞', '配送中', '已到达', '异常'] },
    ],
    columns: [
      { key: 'deliveryNo', label: '配送单号' },
      { key: 'orderNo', label: '订单号' },
      { key: 'storeName', label: '门店' },
      { key: 'droneName', label: '无人机' },
      { key: 'fee', label: '配送费' },
      { key: 'status', label: '状态', toneMap: { 待调度: 'warning', 装载中: 'brand', 已起飞: 'brand', 配送中: 'brand', 已到达: 'success', 异常: 'error' } },
    ],
    rows: [],
  },
  droneFlights: {
    title: '飞行记录',
    filters: [
      { type: 'input', placeholder: '搜索配送单/无人机编号' },
      { type: 'select', placeholder: '飞行状态', options: ['飞行状态', '已完成', '异常', '配送中'] },
    ],
    columns: [
      { key: 'deliveryNo', label: '配送单号' },
      { key: 'droneCode', label: '无人机编号' },
      { key: 'storeName', label: '门店' },
      { key: 'flightDuration', label: '飞行时长' },
      { key: 'waypointCount', label: '轨迹点' },
      { key: 'status', label: '状态', toneMap: { 已完成: 'success', 异常: 'error', 配送中: 'brand' } },
    ],
    rows: [],
  },
  droneFaults: {
    title: '故障管理',
    action: '上报故障',
    filters: [
      { type: 'select', placeholder: '故障状态', options: ['故障状态', '待处理', '处理中', '已解决'] },
      { type: 'select', placeholder: '故障等级', options: ['故障等级', 'NORMAL', 'HIGH', 'URGENT'] },
    ],
    columns: [
      { key: 'faultNo', label: '故障单号' },
      { key: 'droneNo', label: '设备编号' },
      { key: 'type', label: '故障类型' },
      { key: 'level', label: '等级' },
      { key: 'status', label: '状态', toneMap: { 待处理: 'warning', 处理中: 'brand', 已解决: 'success' } },
      { key: 'createdAt', label: '上报时间' },
    ],
    rows: [],
  },
  droneMaintenances: {
    title: '维护管理',
    action: '创建维护单',
    filters: [
      { type: 'select', placeholder: '维护状态', options: ['维护状态', '计划中', '执行中', '已完成'] },
      { type: 'select', placeholder: '维护类型', options: ['维护类型', '维修', '保养', '巡检'] },
    ],
    columns: [
      { key: 'maintenanceNo', label: '维护单号' },
      { key: 'droneNo', label: '设备编号' },
      { key: 'type', label: '类型' },
      { key: 'cost', label: '费用' },
      { key: 'status', label: '状态', toneMap: { 计划中: 'warning', 执行中: 'brand', 已完成: 'success' } },
      { key: 'scheduledAt', label: '计划时间' },
    ],
    rows: [],
  },
  userManagement: {
    title: '用户管理',
    action: '导出用户',
    stats: [
      { label: '累计用户', value: '38,920', sub: '本月 +1,246', tone: 'brand' },
      { label: '今日新增', value: '156', sub: '转化率 12.4%', tone: 'success' },
      { label: '沉睡用户', value: '2,318', sub: '可触达营销', tone: 'warning' },
    ],
    filters: [
      { type: 'input', placeholder: '搜索昵称/手机号' },
      { type: 'select', placeholder: '会员等级', options: ['会员等级', '普通会员', '黄金会员', '黑金会员'] },
    ],
    columns: [
      { key: 'name', label: '用户' },
      { key: 'phone', label: '手机号' },
      { key: 'level', label: '会员等级', toneMap: { 黑金会员: 'brand', 黄金会员: 'warning', 普通会员: 'muted' } },
      { key: 'orders', label: '订单数' },
      { key: 'amount', label: '累计消费' },
      { key: 'last', label: '最近消费' },
    ],
    rows: [
      { name: '王先生', phone: '138****8902', level: '黑金会员', orders: '126', amount: '¥3,286.00', last: '10:45' },
      { name: '李女士', phone: '139****1102', level: '黄金会员', orders: '82', amount: '¥2,108.00', last: '10:42' },
      { name: '张同学', phone: '137****4451', level: '普通会员', orders: '18', amount: '¥386.00', last: '10:35' },
    ],
  },
  userBalance: {
    title: '用户余额',
    action: '余额调整',
    columns: [
      { key: 'name', label: '用户' },
      { key: 'balance', label: '可用余额' },
      { key: 'status', label: '账户状态', toneMap: { 正常: 'success', 冻结: 'error' } },
      { key: 'time', label: '更新时间' },
    ],
    rows: [
      { name: '王先生', balance: '¥286.00', status: '正常', time: '10:45' },
      { name: '李女士', balance: '¥68.50', status: '正常', time: '10:42' },
      { name: '陈女士', balance: '¥128.00', status: '冻结', time: '09:58' },
    ],
  },
  userPoints: {
    title: '用户积分',
    action: '积分调整',
    columns: [
      { key: 'name', label: '用户' },
      { key: 'points', label: '可用积分' },
      { key: 'growth', label: '成长值' },
      { key: 'level', label: '会员等级' },
      { key: 'last', label: '最近变动' },
    ],
    rows: [
      { name: '王先生', points: '12,680', growth: '8,920', level: '黑金会员', last: '+36 消费赠送' },
      { name: '李女士', points: '8,210', growth: '4,860', level: '黄金会员', last: '+22 消费赠送' },
      { name: '张同学', points: '980', growth: '520', level: '普通会员', last: '-150 兑换优惠券' },
    ],
  },
  userAssets: {
    title: '资产明细',
    filters: [
      { type: 'input', placeholder: '搜索用户/流水号' },
      { type: 'select', placeholder: '变动类型', options: ['变动类型', '余额充值', '积分发放', '优惠券核销'] },
    ],
    columns: [
      { key: 'id', label: '流水号' },
      { key: 'user', label: '用户' },
      { key: 'type', label: '类型' },
      { key: 'amount', label: '变动值' },
      { key: 'source', label: '来源' },
      { key: 'time', label: '时间' },
    ],
    rows: [
      { id: 'ZC20231024001', user: '王先生', type: '余额消费', amount: '-¥36.00', source: '订单支付', time: '10:45:22' },
      { id: 'ZC20231024002', user: '李女士', type: '积分发放', amount: '+22', source: '消费赠送', time: '10:42:10' },
      { id: 'ZC20231024003', user: '张同学', type: '优惠券核销', amount: '-¥2.00', source: '订单优惠', time: '10:35:05' },
    ],
  },
  memberLevels: {
    title: '会员等级',
    action: '新增等级',
    cards: [
      { title: '普通会员', status: '0 成长值', meta: ['消费返 1 倍积分', '生日券 1 张', '用户数 24,680'], footer: '默认等级' },
      { title: '黄金会员', status: '3000 成长值', meta: ['消费返 1.2 倍积分', '每月 8.8 折券', '用户数 10,120'], footer: '升级率 26%' },
      { title: '黑金会员', status: '8000 成长值', meta: ['消费返 1.5 倍积分', '专属客服', '用户数 4,120'], footer: '高价值用户' },
    ],
  },
  memberRules: {
    title: '规则配置',
    action: '新增规则',
    columns: [
      { key: 'type', label: '规则类型', toneMap: { 成长值: 'brand', 积分: 'success' } },
      { key: 'scene', label: '场景' },
      { key: 'rule', label: '发放规则' },
      { key: 'limit', label: '上限/有效期' },
      { key: 'status', label: '状态', toneMap: { 启用: 'success', 停用: 'muted' } },
    ],
    rows: [
      { type: '成长值', scene: '订单实付', rule: '每消费 1 元得 1 成长值', limit: '每日 500', status: '启用' },
      { type: '成长值', scene: '首次下单', rule: '一次性赠送 200', limit: '一次', status: '启用' },
      { type: '成长值', scene: '评价订单', rule: '每次赠送 20', limit: '每日 3 次', status: '停用' },
      { type: '积分', scene: '订单支付', rule: '实付每 1 元得 1 积分', limit: '365 天', status: '启用' },
      { type: '积分', scene: '签到奖励', rule: '每日签到 5 积分', limit: '180 天', status: '启用' },
      { type: '积分', scene: '邀请好友', rule: '好友首单赠 200 积分', limit: '365 天', status: '启用' },
    ],
  },
  memberBenefits: {
    title: '会员权益',
    action: '配置权益',
    cards: [
      { title: '生日专享券', status: '启用', meta: ['适用等级: 全部会员', '券面额 ¥10', '领取后 30 天有效'], footer: '每年 1 次' },
      { title: '黑金免配送费', status: '启用', meta: ['适用等级: 黑金会员', '每日 2 次', '最高抵扣 ¥8'], footer: '提升外卖复购' },
      { title: '新品试饮', status: '待上线', meta: ['适用等级: 黄金及以上', '每月 1 次', '限指定门店'], footer: '计划 11 月上线' },
    ],
  },
  marketingCoupons: {
    title: '优惠券',
    action: '新建优惠券',
    stats: [
      { label: '发放中', value: '18', sub: '覆盖 9 家门店', tone: 'brand' },
      { label: '今日领取', value: '2,460', sub: '核销率 18.6%', tone: 'success' },
      { label: '即将过期', value: '6', sub: '需运营提醒', tone: 'warning' },
    ],
    columns: [
      { key: 'name', label: '券名称' },
      { key: 'type', label: '类型' },
      { key: 'value', label: '优惠' },
      { key: 'stock', label: '库存' },
      { key: 'status', label: '状态', toneMap: { 发放中: 'success', 待开始: 'warning', 已结束: 'muted' } },
    ],
    rows: [
      { name: '新人首单券', type: '满减券', value: '满 20 减 8', stock: '3,286', status: '发放中' },
      { name: '下午茶补贴', type: '折扣券', value: '8.8 折', stock: '1,120', status: '待开始' },
      { name: '黑金会员券', type: '立减券', value: '减 12', stock: '420', status: '已结束' },
    ],
  },
  marketingPointsMall: {
    title: '积分商城',
    action: '新增兑换项',
    stats: [
      { label: '上架兑换项', value: '6', sub: '优惠券 3 / 实物 3', tone: 'brand' },
      { label: '今日兑换', value: '128', sub: '较昨日 +16%', tone: 'success' },
      { label: '低库存实物', value: '2', sub: '需补货或下架', tone: 'warning' },
      { label: '消耗积分', value: '46,800', sub: '本周累计', tone: 'success' },
    ],
    filters: [
      { type: 'input', placeholder: '搜索兑换项名称' },
      { type: 'select', placeholder: '兑换类型', options: ['全部类型', '优惠券', '实物'] },
      { type: 'select', placeholder: '上架状态', options: ['全部状态', '上架', '下架', '售罄'] },
    ],
    columns: [
      { key: 'name', label: '兑换项名称' },
      { key: 'type', label: '兑换类型' },
      { key: 'cost', label: '所需积分', align: 'right' },
      { key: 'reward', label: '兑换内容' },
      { key: 'stock', label: '库存', align: 'right' },
      { key: 'limit', label: '限制' },
      { key: 'status', label: '状态', toneMap: { 上架: 'success', 下架: 'muted', 售罄: 'warning' } },
    ],
    rows: [
      { name: '饮品满20减5券', type: '优惠券', cost: '260', reward: '满 20 减 5', stock: '3,286', limit: '每人 1 张', status: '上架' },
      { name: '配送立减3元券', type: '优惠券', cost: '180', reward: '外送订单立减', stock: '1,120', limit: '每人 2 张', status: '上架' },
      { name: '青屿随行杯', type: '实物', cost: '1,280', reward: '门店自提周边', stock: '36', limit: '每人 1 件', status: '上架' },
      { name: '云咖帆布袋', type: '实物', cost: '980', reward: '7 天内门店领取', stock: '8', limit: '每人 1 件', status: '上架' },
      { name: '限定甜品兑换卡', type: '实物', cost: '680', reward: '指定甜品 1 份', stock: '0', limit: '每人 1 件', status: '售罄' },
    ],
    cards: [
      { title: '优惠券兑换配置', status: '已配置', meta: ['支持选择优惠券模板', '配置兑换积分、库存、每人兑换上限', '兑换成功后自动进入用户卡包'], footer: '用于下单抵扣' },
      { title: '实物兑换配置', status: '已配置', meta: ['支持配置实物名称、图片、库存', '配置领取方式和有效期', '低库存时提醒运营补货'], footer: '用于周边与门店礼品' },
    ],
  },
  marketingCards: {
    title: '周卡月卡',
    action: '新建卡包',
    cards: [
      { title: '咖啡周卡', status: '¥39.90', meta: ['7 天内可用 5 次', '每次立减 ¥6', '已售 1,246 张'], footer: '复购率 42%' },
      { title: '轻享月卡', status: '¥99.00', meta: ['30 天内可用 20 次', '每次立减 ¥5', '已售 680 张'], footer: '会员转化主推' },
      { title: '外卖免配卡', status: '¥19.90', meta: ['30 天内 10 次免配送', '最高抵扣 ¥8', '已售 328 张'], footer: '配送场景专用' },
    ],
  },
  marketingRecharge: {
    title: '充值套餐',
    action: '新增套餐',
    columns: [
      { key: 'name', label: '套餐名称' },
      { key: 'pay', label: '支付金额' },
      { key: 'gift', label: '赠送金额' },
      { key: 'sales', label: '销量' },
      { key: 'status', label: '状态', toneMap: { 上架: 'success', 下架: 'muted' } },
    ],
    rows: [
      { name: '轻量充值', pay: '¥100.00', gift: '¥8.00', sales: '2,318', status: '上架' },
      { name: '高频用户充值', pay: '¥300.00', gift: '¥36.00', sales: '1,086', status: '上架' },
      { name: '企业团购充值', pay: '¥1000.00', gift: '¥150.00', sales: '128', status: '下架' },
    ],
  },
  marketingBanners: {
    title: '首页 Banner',
    action: '新增 Banner',
    columns: [
      { key: 'title', label: '标题' },
      { key: 'image', label: '图片' },
      { key: 'link', label: '跳转' },
      { key: 'sort', label: '排序' },
      { key: 'status', label: '状态', toneMap: { 启用: 'success', 停用: 'muted' } },
    ],
    rows: [
      { title: '新品上市', image: 'https://example.com/banner1.png', link: 'PRODUCT:1', sort: '1', status: '启用' },
      { title: '会员日专享', image: 'https://example.com/banner2.png', link: 'ACTIVITY:flash-sale', sort: '2', status: '启用' },
    ],
  },
  groupBuy: {
    title: '拼团活动',
    action: '创建拼团',
    columns: [
      { key: 'name', label: '活动名称' },
      { key: 'product', label: '商品' },
      { key: 'people', label: '成团人数' },
      { key: 'price', label: '拼团价' },
      { key: 'status', label: '状态', toneMap: { 进行中: 'success', 待开始: 'warning', 已结束: 'muted' } },
    ],
    rows: [
      { name: '两人成团喝拿铁', product: '招牌生椰拿铁', people: '2 人', price: '¥15.90', status: '进行中' },
      { name: '宿舍下午茶团', product: '多肉葡萄冰沙', people: '4 人', price: '¥19.90', status: '待开始' },
      { name: '轻食尝鲜团', product: '牛角包套餐', people: '3 人', price: '¥22.90', status: '已结束' },
    ],
  },
  marketingBargain: {
    title: '砍价活动',
    action: '创建砍价',
    columns: [
      { key: 'name', label: '活动名称' },
      { key: 'floor', label: '最低价' },
      { key: 'assist', label: '助力人数' },
      { key: 'join', label: '参与人数' },
      { key: 'status', label: '状态', toneMap: { 进行中: 'success', 待开始: 'warning', 已结束: 'muted' } },
    ],
    rows: [
      { name: '9.9 元喝生椰', floor: '¥9.90', assist: '3-5 人', join: '1,208', status: '进行中' },
      { name: '第二杯半价砍', floor: '¥6.00', assist: '2-4 人', join: '860', status: '待开始' },
      { name: '新品试饮砍', floor: '¥1.00', assist: '5 人', join: '3,120', status: '已结束' },
    ],
  },
  marketingInvite: {
    title: '邀请有礼',
    action: '配置奖励',
    stats: [
      { label: '今日邀请', value: '326', sub: '有效 186 人', tone: 'success' },
      { label: '首单转化', value: '42.8%', sub: '较上周 +5.2%', tone: 'brand' },
      { label: '奖励发放', value: '¥1,860', sub: '预算内', tone: 'warning' },
    ],
    columns: [
      { key: 'name', label: '活动名称' },
      { key: 'inviter', label: '邀请人奖励' },
      { key: 'newUser', label: '新用户奖励' },
      { key: 'valid', label: '有效条件' },
      { key: 'status', label: '状态', toneMap: { 启用: 'success', 暂停: 'warning' } },
    ],
    rows: [
      { name: '好友首单奖励', inviter: '¥8 余额', newUser: '满 20 减 8', valid: '好友首单实付满 15', status: '启用' },
      { name: '校园裂变', inviter: '200 积分', newUser: '新人咖啡券', valid: '新注册学生用户', status: '暂停' },
    ],
  },
  flashSale: {
    title: '限时秒杀',
    action: '创建秒杀',
    columns: [
      { key: 'name', label: '秒杀场次' },
      { key: 'product', label: '商品' },
      { key: 'price', label: '秒杀价' },
      { key: 'stock', label: '库存' },
      { key: 'status', label: '状态', toneMap: { 抢购中: 'success', 预热中: 'warning', 已结束: 'muted' } },
    ],
    rows: [
      { name: '10:00 咖啡闪购', product: '招牌生椰拿铁', price: '¥12.90', stock: '86/200', status: '抢购中' },
      { name: '14:00 下午茶', product: '经典珍珠奶茶', price: '¥9.90', stock: '200/200', status: '预热中' },
      { name: '昨日补贴场', product: '厚乳拿铁', price: '¥13.90', stock: '0/120', status: '已结束' },
    ],
  },
  dataOverview: {
    title: '经营概览',
    stats: dashboardStats.slice(0, 4),
    columns: [
      { key: 'metric', label: '指标' },
      { key: 'today', label: '今日' },
      { key: 'yesterday', label: '昨日' },
      { key: 'trend', label: '趋势', toneMap: { 上升: 'success', 下降: 'error', 持平: 'muted' } },
    ],
    rows: [
      { metric: '营业额', today: '¥12,450.00', yesterday: '¥11,066.00', trend: '上升' },
      { metric: '订单数', today: '456', yesterday: '421', trend: '上升' },
      { metric: '退款率', today: '1.8%', yesterday: '2.1%', trend: '下降' },
    ],
  },
  dataOrders: {
    title: '订单分析',
    stats: [
      { label: '支付订单', value: '1,245', sub: '转化率 68.5%', tone: 'success' },
      { label: '取消订单', value: '86', sub: '取消率 4.2%', tone: 'warning' },
      { label: '退款订单', value: '12', sub: '退款率 1.8%', tone: 'error' },
    ],
    columns: orderColumns,
    rows: orderRows,
  },
  dataProducts: {
    title: '商品分析',
    stats: [
      { label: '动销商品', value: '86', sub: '动销率 68.2%', tone: 'success' },
      { label: '销售冠军', value: '生椰拿铁', sub: '156 杯', tone: 'brand' },
      { label: '滞销商品', value: '12', sub: '需优化', tone: 'warning' },
    ],
    columns: [
      { key: 'name', label: '商品' },
      { key: 'sales', label: '销量' },
      { key: 'amount', label: '销售额' },
      { key: 'rate', label: '转化率' },
      { key: 'trend', label: '趋势', toneMap: { 上升: 'success', 下降: 'error' } },
    ],
    rows: [
      { name: '招牌生椰拿铁', sales: '156', amount: '¥2,964.00', rate: '18.6%', trend: '上升' },
      { name: '多肉葡萄冰沙', sales: '124', amount: '¥3,100.00', rate: '15.2%', trend: '上升' },
      { name: '经典珍珠奶茶', sales: '98', amount: '¥1,470.00', rate: '11.8%', trend: '下降' },
    ],
  },
  storeRankPage: {
    title: '门店排行',
    columns: [
      { key: 'rank', label: '排名' },
      { key: 'name', label: '门店' },
      { key: 'amount', label: '营业额' },
      { key: 'orders', label: '订单数' },
      { key: 'price', label: '客单价' },
      { key: 'trend', label: '趋势', toneMap: { '+15%': 'success', '+8%': 'success', '-2%': 'error', '+5%': 'success' } },
    ],
    rows: storeRank.map((item, index) => ({ rank: String(index + 1), ...item })),
  },
  dataUsers: {
    title: '用户分析',
    stats: [
      { label: '新增用户', value: '156', sub: '自然流量 62%', tone: 'success' },
      { label: '复购用户', value: '1,086', sub: '复购率 38.2%', tone: 'brand' },
      { label: '流失预警', value: '320', sub: '30 天未消费', tone: 'warning' },
    ],
    columns: [
      { key: 'segment', label: '用户分层' },
      { key: 'count', label: '用户数' },
      { key: 'amount', label: '消费额' },
      { key: 'rate', label: '占比' },
      { key: 'action', label: '运营动作' },
    ],
    rows: [
      { segment: '高价值用户', count: '4,120', amount: '¥286,000', rate: '10.6%', action: '会员权益触达' },
      { segment: '活跃用户', count: '18,680', amount: '¥520,000', rate: '48.0%', action: '新品券推送' },
      { segment: '沉睡用户', count: '2,318', amount: '¥18,600', rate: '6.0%', action: '召回活动' },
    ],
  },
  transactions: {
    title: '交易流水',
    action: '导出流水',
    columns: [
      { key: 'id', label: '流水号' },
      { key: 'order', label: '订单号' },
      { key: 'channel', label: '支付渠道' },
      { key: 'amount', label: '金额' },
      { key: 'status', label: '状态', toneMap: { 成功: 'success', 处理中: 'warning', 失败: 'error' } },
      { key: 'time', label: '交易时间' },
    ],
    rows: [
      { id: 'LS202310240001', order: 'DD202310240001', channel: '微信支付', amount: '¥36.00', status: '成功', time: '10:45:25' },
      { id: 'LS202310240002', order: 'DD202310240002', channel: '支付宝', amount: '¥22.00', status: '成功', time: '10:42:13' },
      { id: 'LS202310240003', order: 'DD202310240018', channel: '微信支付', amount: '¥19.00', status: '处理中', time: '10:30:02' },
    ],
  },
  reconciliation: {
    title: '门店对账',
    action: '生成对账单',
    columns: [
      { key: 'store', label: '门店' },
      { key: 'orders', label: '订单数' },
      { key: 'amount', label: '应收金额' },
      { key: 'settled', label: '已结算' },
      { key: 'diff', label: '差异' },
      { key: 'status', label: '状态', toneMap: { 已平账: 'success', 待确认: 'warning' } },
    ],
    rows: [
      { store: '高新科技园店', orders: '120', amount: '¥3,450.00', settled: '¥3,450.00', diff: '¥0.00', status: '已平账' },
      { store: '万象城店', orders: '98', amount: '¥2,890.00', settled: '¥2,880.00', diff: '¥10.00', status: '待确认' },
      { store: '大学城店', orders: '85', amount: '¥2,100.00', settled: '¥2,100.00', diff: '¥0.00', status: '已平账' },
    ],
  },
  refundTransactions: {
    title: '退款流水',
    action: '导出流水',
    columns: [
      { key: 'id', label: '退款流水' },
      { key: 'order', label: '订单号' },
      { key: 'channel', label: '原支付渠道' },
      { key: 'amount', label: '退款金额' },
      { key: 'status', label: '状态', toneMap: { 已退回: 'success', 处理中: 'warning', 失败: 'error' } },
      { key: 'time', label: '退款时间' },
    ],
    rows: [
      { id: 'TKLS20231024001', order: 'DD202310240004', channel: '微信支付', amount: '¥58.00', status: '已退回', time: '10:21:04' },
      { id: 'TKLS20231024002', order: 'DD202310240015', channel: '余额支付', amount: '¥24.00', status: '处理中', time: '10:14:19' },
      { id: 'TKLS20231024003', order: 'DD202310240020', channel: '支付宝', amount: '¥18.00', status: '失败', time: '09:45:42' },
    ],
  },
  paymentConfig: {
    title: '支付配置',
    action: '保存配置',
    cards: [
      { title: '微信支付', status: '已启用', meta: ['商户号: 190000****', '小程序支付 已开通', '证书状态 正常'], footer: '最近校验: 10:40' },
      { title: '支付宝', status: '已启用', meta: ['应用 ID: 2023****', '生活号支付 已开通', '回调正常'], footer: '最近校验: 10:32' },
      { title: '余额支付', status: '已启用', meta: ['支持组合支付', '退款原路退余额', '风控阈值 ¥500'], footer: '内部账户体系' },
    ],
  },
  profitSharing: {
    title: '微信分账',
  },
  profitSharingRules: {
    title: '分账规则',
    action: '新增规则',
  },
  profitSharingReceivers: {
    title: '分账接收方',
    action: '新增接收方',
  },
  messageLogs: {
    title: '消息记录',
    columns: [
      { key: 'id', label: '消息 ID' },
      { key: 'scene', label: '场景' },
      { key: 'receiver', label: '接收人' },
      { key: 'channel', label: '渠道' },
      { key: 'status', label: '状态', toneMap: { 已送达: 'success', 发送中: 'warning', 失败: 'error' } },
      { key: 'time', label: '发送时间' },
    ],
    rows: [
      { id: 'MSG20231024001', scene: '订单完成', receiver: '王先生', channel: '小程序订阅消息', status: '已送达', time: '10:46:00' },
      { id: 'MSG20231024002', scene: '退款通知', receiver: '赵先生', channel: '短信', status: '发送中', time: '10:22:15' },
      { id: 'MSG20231024003', scene: '优惠券到期', receiver: '陈女士', channel: '站内信', status: '失败', time: '09:30:12' },
    ],
  },
  messageTemplates: {
    title: '通知模板',
    action: '新增模板',
    columns: [
      { key: 'name', label: '模板名称' },
      { key: 'scene', label: '触发场景' },
      { key: 'channel', label: '渠道' },
      { key: 'status', label: '状态', toneMap: { 启用: 'success', 草稿: 'warning', 停用: 'muted' } },
      { key: 'time', label: '更新时间' },
    ],
    rows: [
      { name: '订单完成通知', scene: '订单状态变更', channel: '订阅消息', status: '启用', time: '10-22 16:30' },
      { name: '退款进度通知', scene: '退款处理', channel: '短信', status: '启用', time: '10-20 11:08' },
      { name: '会员升级提醒', scene: '会员成长', channel: '站内信', status: '草稿', time: '10-18 09:48' },
    ],
  },
  messageConfig: {
    title: '推送配置',
    action: '保存配置',
    cards: [
      { title: '订阅消息', status: '已启用', meta: ['订单提醒 100%', '营销提醒 需用户授权', '失败重试 2 次'], footer: '小程序原生能力' },
      { title: '短信通道', status: '已启用', meta: ['签名: 交发云咖', '余额 18,600 条', '异常告警开启'], footer: '用于关键通知' },
      { title: '站内信', status: '已启用', meta: ['保留 180 天', '未读红点提醒', '支持营销模板'], footer: '低成本触达' },
    ],
  },
  roles: {
    title: '角色权限',
    action: '新增角色',
    columns: [
      { key: 'name', label: '角色名称' },
      { key: 'users', label: '成员数' },
      { key: 'scope', label: '数据范围' },
      { key: 'status', label: '状态', toneMap: { 启用: 'success', 停用: 'muted' } },
      { key: 'time', label: '更新时间' },
    ],
    rows: [
      { name: '系统超管', users: '2', scope: '全部门店', status: '启用', time: '10-24 10:10' },
      { name: '门店店长', users: '12', scope: '所属门店', status: '启用', time: '10-22 15:20' },
      { name: '财务专员', users: '3', scope: '财务模块', status: '启用', time: '10-18 09:08' },
    ],
  },
  systemAdmins: {
    title: '管理员',
    action: '新增管理员',
    columns: [
      { key: 'name', label: '管理员' },
      { key: 'role', label: '角色' },
      { key: 'store', label: '管理门店' },
      { key: 'status', label: '状态', toneMap: { 在线: 'success', 离线: 'muted', 禁用: 'error' } },
      { key: 'last', label: '最近登录' },
    ],
    rows: [
      { name: '演示管理员', role: '系统超管', store: '全部门店', status: '在线', last: '10:45:22' },
      { name: '张店长', role: '门店店长', store: '高新科技园店', status: '离线', last: '昨日 18:30' },
      { name: '财务 Amy', role: '财务专员', store: '全部门店', status: '禁用', last: '10-20 09:12' },
    ],
  },
  operationLogs: {
    title: '操作日志',
    filters: [
      { type: 'input', placeholder: '搜索操作人/内容' },
      { type: 'select', placeholder: '操作模块', options: ['操作模块', '订单中心', '商品中心', '系统管理'] },
    ],
    columns: [
      { key: 'operator', label: '操作人' },
      { key: 'module', label: '模块' },
      { key: 'content', label: '操作内容' },
      { key: 'ip', label: 'IP' },
      { key: 'time', label: '时间' },
    ],
    rows: [
      { operator: '演示管理员', module: '订单中心', content: '导出订单报表', ip: '10.10.12.8', time: '10:45:22' },
      { operator: '张店长', module: '商品中心', content: '调整库存', ip: '10.10.18.21', time: '10:12:08' },
      { operator: '财务 Amy', module: '财务与支付', content: '生成对账单', ip: '10.10.9.16', time: '09:40:30' },
    ],
  },
  systemParams: {
    title: '系统参数',
    action: '保存参数',
    cards: [
      { title: '订单参数', status: '已配置', meta: ['自动取消: 15 分钟', '接单超时: 3 分钟', '售后期限: 24 小时'], footer: '影响交易履约' },
      { title: '积分参数', status: '已配置', meta: ['积分有效期: 365 天', '抵扣比例: 100 积分 = ¥1', '单笔上限: 20%'], footer: '影响会员资产' },
      { title: '告警参数', status: '已配置', meta: ['库存预警: 60', '打印机离线: 5 分钟', '退款超时: 10 分钟'], footer: '影响运营监控' },
    ],
  },
}
